/**
 * Post-login target sanitisation.
 *
 * `proxy.ts` stamps the path a visitor was bounced off as `?returnTo=`, and that
 * value travels through the login page, the sealed OIDC state cookie, the
 * callback and the instance picker before anything navigates to it. Exactly one
 * rule governs all of those hops, so it lives in exactly one place: **a
 * same-origin absolute path, or `/`**.
 *
 * Rejected, and why each matters:
 * - `https://evil.test/…` — an absolute URL would be an open redirect.
 * - `//evil.test/…`       — protocol-relative; a browser treats it as absolute.
 * - `/\evil.test/…`       — some URL parsers normalise the backslash to `/`.
 * - a control character   — an encoded CR/LF that survived decoding has no
 *                           business in a `Location` header.
 * - anything not starting with `/` — a relative path would resolve against
 *   whatever route happens to be current.
 *
 * The default is `/` (the app root), never the raw input, so a malformed value
 * degrades to "log in normally" rather than to a redirect the caller controls.
 */
export function safeReturnTo(raw: string | null | undefined): string {
  if (typeof raw !== "string") return "/";
  if (!raw.startsWith("/")) return "/";
  if (raw.startsWith("//") || raw.startsWith("/\\")) return "/";
  for (const char of raw) {
    const code = char.codePointAt(0)!;
    if (code < 0x20 || code === 0x7f) return "/";
  }
  return raw;
}

/** True when `target` is worth carrying through the flow at all. */
export function hasReturnTo(target: string): boolean {
  return target !== "/" && target.length > 0;
}

/** `path` with `?returnTo=` appended when the target is not just the app root. */
export function withReturnTo(path: string, target: string): string {
  const safe = safeReturnTo(target);
  return hasReturnTo(safe) ? `${path}?returnTo=${encodeURIComponent(safe)}` : path;
}
